import React, { useState, useEffect, useContext } from 'react'
import { Table, Button, Popconfirm, notification, Tag } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { GET_KEYWORD_MAPPING } from '../graphql/Queries'
import {
  CREATE_KEYWORD_MAPPING,
  UPDATE_KEYWORD_MAPPING,
  DELETE_KEYWORD_MAPPING,
} from '../graphql/Mutations'
import KeywordMappingModal from './KeywordMappingModal'
import EditKeywordMappingModel from './EditKeywordMappingModel'
import { AppContext } from 'AppContext'
import { canUserEdit, canUserAdd, canUserDelete } from 'utils/User'
const PAGE_SIZE = 10

// const getPriorityColor = (priority) => {
//   switch (priority) {
//     case 'HIGH':
//       return 'red'
//     case 'MEDIUM':
//       return 'orange'
//     case 'LOW':
//       return 'green'
//     default:
//       return 'default'
//   }
// }

export default function KeywordMapping() {
  const { state: { currentUser, userPermissions, isClient } = {} } = useContext(AppContext) || {}
  const canEdit = canUserEdit(userPermissions || currentUser, isClient)
  const canAdd = canUserAdd(userPermissions || currentUser, isClient)
  const canDelete = canUserDelete(userPermissions || currentUser, isClient)

  const { data, loading, refetch } = useQuery(GET_KEYWORD_MAPPING)
  const [createKeywordMapping] = useMutation(CREATE_KEYWORD_MAPPING)
  const [updateKeywordMapping] = useMutation(UPDATE_KEYWORD_MAPPING)
  const [deleteKeywordMapping] = useMutation(DELETE_KEYWORD_MAPPING)

  const [modalVisible, setModalVisible] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [keywordMappings, setKeywordMappings] = useState([])
  // const [groupedKeywordMappings, setGroupedKeywordMappings] = useState([])
  const [modalLoading, setModalLoading] = useState(false)

  useEffect(() => {
    if (data && data.getKeywordMappings) {
      setKeywordMappings(data.getKeywordMappings)
    }
  }, [data])

  const handleAdd = () => {
    if (!canAdd) return
    setEditingItem(null)
    setModalVisible(true)
  }


  const handleEdit = (record) => {
    if (!canEdit) return
    const freshRecord = keywordMappings.find(
      (item) =>
        item?.department?.id === record?.department?.id &&
        item?.team?.id === record?.team?.id
    )
    setEditingItem(freshRecord)
    setEditModalVisible(true)
  }

  const handleDelete = async (ids) => {
    if (!canDelete) return
    try {
      for (const item of ids) {
        await deleteKeywordMapping({ variables: { id: item?.id } })
      }
    } catch (err) {
      notification.error({ message: 'Delete failed' })
    } finally {
      notification.success({ message: 'Keyword mapping(s) deleted' })
      refetch()
    }
  }

  const handleModalOk = async (values) => {
    if (editingItem && !canEdit) return
    if (!editingItem && !canAdd) return
    setModalLoading(true)
    try {
      if (editingItem) {
        await updateKeywordMapping({
          variables: { id: editingItem.id, data: values },
        })
        notification.success({ message: 'Keyword mapping updated' })
      } else {
        await createKeywordMapping({ variables: { data: values } })
        notification.success({ message: 'Keyword mapping created' })
      }
      setModalVisible(false)
      refetch()
    } catch (err) {
      notification.error({ message: 'Operation failed' })
    } finally {
      setModalLoading(false)
    }
  }

  const columns = [
    {
      title: 'Department',
      key: 'department',
      render: (_, record) => record.department?.departmentName,
    },
    {
      title: 'Team',
      key: 'team',
      render: (_, record) => record.team?.teamName,
    },
    {
      title: 'Keywords',
      key: 'keywords',
      render: (_, record) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {record.keywords.map((keyword, index) => (
            <Tag
              key={index}
              color={keyword.isActive ? 'blue' : 'default'}
              style={{ marginBottom: 4 }}
            >
              {keyword.keyword}
            </Tag>
          ))}
        </div>
      ),
    },
    ...((canEdit || canDelete)
      ? [
          {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
              <>
                {canEdit && (
                  <EditOutlined
                    color='primary'
                    style={{ marginRight: 12 }}
                    onClick={() => handleEdit(record)}
                  />
                )}
                {canDelete && (
                  <Popconfirm
                    title='Delete?'
                    onConfirm={() => handleDelete(record?.keywords)}
                  >
                    <DeleteOutlined color='red' />
                  </Popconfirm>
                )}
              </>
            ),
          },
        ]
      : []),
  ]

  const paginatedData = keywordMappings.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  )

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 18 }}>Keyword Mapping</div>
        {canAdd && (
          <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
            Add Keyword Mapping
          </Button>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={paginatedData}
        rowKey='id'
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total: keywordMappings?.length || 0,
          onChange: setCurrentPage,
        }}
      />
      <KeywordMappingModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSave={handleModalOk}
        initialValues={editingItem}
        loading={modalLoading}
      />
      <EditKeywordMappingModel
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        onSave={handleModalOk}
        initialValues={editingItem}
        loading={modalLoading}
        parentRefetch={refetch}
      />
    </div>
  )
}
